/**
 * SessionsScreen — active sessions, per-row revoke, and log-out-everywhere.
 * STORY-001-010 Implementation logic §4 + AC #3, #4, #5.
 *
 * - Lists the user's active refresh-token families (GET /me/sessions).
 * - Each row shows the device, the masked IP and when it was last used; the
 *   current session is marked and cannot revoke itself (you leave it via
 *   Settings → Log out).
 * - "Log out everywhere else" calls POST /me/sessions/revoke-others.
 * - Revokes are optimistic: the row leaves immediately and the list re-reads on
 *   failure.
 *
 * THE 2026-08 REBUILD
 *
 * Before: rounded bordered boxes with the device on one line and
 * "IP: 10.0.0.x · Последняя активность: 6/7/2026, 9:00:00 AM" on the next, in
 * body type, at `text-muted-foreground`. Three problems: the figures were prose
 * (so no two rows aligned), the raw `toLocaleString()` output ignored the UI
 * language entirely, and the "Revoke" button never said what revoking DOES.
 *
 * After: a ledger. One ruled sheet, one row per session, every figure — the
 * masked IP, the timestamp — in `.g-data` so a column of them scans vertically.
 * The current session carries a drawn marker, not just a tint. And the section
 * stub states the consequence in advance ("the device will have to sign in
 * again"), with the per-device confirmation of what just happened carried by
 * the toast.
 *
 * ONE DELIBERATE DEVIATION from the brief, called out honestly: revoke is NOT
 * behind a confirmation dialog. `account-sessions-revoke.spec.ts` asserts that
 * a single click on `session-revoke-<id>` removes the row (AC #4/#5), and the
 * brief also requires that e2e behaviour stay intact. Revoking is additionally
 * the mildest of the three destructive actions — the device signs in again and
 * loses nothing — so the confirmation budget is spent where it matters (log
 * out, delete account). Both revoke paths instead state their consequence
 * BEFORE the click and name what happened after it.
 *
 * Test ids (`sessions-screen`, `sessions-list`, `session-row-*`,
 * `session-current-*`, `session-revoke-*`, `sessions-logout-everywhere`,
 * `sessions-loading`, `sessions-empty`, `sessions-load-error`) are preserved.
 */
import clsx from "clsx";
import { useCallback, useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button, Skeleton } from "@garden/ui";

import { Stagger, StaggerItem } from "@/components/motion";
import {
  DangerZone,
  Slip,
  SlipMessage,
  SlipRow,
  SlipSection,
} from "@/features/account/components/Slip";
import {
  getSessions,
  registerAccountI18n,
  revokeOtherSessions,
  revokeSession,
  type SessionInfo,
} from "@/features/account/api";
import { NetworkError } from "@/lib/api-client";
import { notify } from "@/lib/notify";

/** The failure the user actually hit — a dead connection reads differently. */
function errorDescriptionKey(err: unknown): string {
  return err instanceof NetworkError ? "errors.network" : "errors.generic";
}

/**
 * Turn a raw user-agent into something a person recognises.
 *
 * The list previously printed the user-agent verbatim — "Mozilla/5.0
 * (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko)…"
 * — truncated at the row's edge. For an audience of first-time renovators that
 * is not a device name, it is noise, and it makes the ONE question this screen
 * exists to answer ("is one of these not me?") unanswerable.
 *
 * The parse is deliberately shallow and order-sensitive (Edge/Opera/Yandex all
 * carry "Chrome" in their UA, so they are tested first). When it cannot tell,
 * the caller falls back to the localized "unknown device" label rather than
 * inventing one — and the full UA stays available as the row's `title`.
 */
function describeDevice(userAgent: string | null): string | null {
  if (userAgent == null || userAgent.trim() === "") return null;
  const ua = userAgent;
  const browser =
    /Edg\//.test(ua) ? "Edge"
    : /OPR\/|Opera/.test(ua) ? "Opera"
    : /YaBrowser/.test(ua) ? "Yandex"
    : /SamsungBrowser/.test(ua) ? "Samsung Internet"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari"
    : null;
  const platform =
    /iPhone/.test(ua) ? "iPhone"
    : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) ? "Android"
    : /Windows/.test(ua) ? "Windows"
    : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Linux/.test(ua) ? "Linux"
    : null;
  if (browser === null && platform === null) return null;
  return [browser, platform].filter(Boolean).join(" · ");
}

/**
 * A timestamp a human can place, in the user's own language. The previous
 * `toLocaleString()` used the browser locale, so a Kyrgyz UI printed US dates.
 * Falls back to the raw ISO string if parsing fails (defensive).
 */
function formatTimestamp(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 16).replace("T", " ");
  }
}

export function SessionsScreen(): ReactElement {
  registerAccountI18n();
  const { t, i18n } = useTranslation("account");
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokingOthers, setRevokingOthers] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await getSessions();
      setSessions(data.sessions);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onRevoke = async (session: SessionInfo): Promise<void> => {
    const device = describeDevice(session.user_agent) ?? t("sessions.unknownDevice");
    setRevokingId(session.family_id);
    const prev = sessions;
    // Optimistic removal — the row leaves at the moment of the click.
    setSessions((cur) => (cur ?? []).filter((s) => s.family_id !== session.family_id));
    try {
      await revokeSession(session.family_id);
      notify.success(t("sessions.revokedToast", { device }), {
        description: t("sessions.revokedNote"),
        id: "session-revoke",
      });
    } catch (err) {
      setSessions(prev); // revert — the row comes back where it was
      notify.error(t("sessions.revokeFailed", { device }), {
        description: t(errorDescriptionKey(err)),
        id: "session-revoke",
        action: { label: t("sessions.retry"), onClick: () => void onRevoke(session) },
      });
    } finally {
      setRevokingId(null);
    }
  };

  const onRevokeOthers = async (): Promise<void> => {
    setRevokingOthers(true);
    const prev = sessions;
    // Optimistically keep only the current session.
    setSessions((cur) => (cur ?? []).filter((s) => s.is_current));
    try {
      await revokeOtherSessions();
      notify.success(t("sessions.revokedOthersToast"), {
        description: t("sessions.revokedNote"),
        id: "session-revoke-others",
      });
    } catch (err) {
      setSessions(prev);
      notify.error(t("sessions.revokeOthersFailed"), {
        description: t(errorDescriptionKey(err)),
        id: "session-revoke-others",
        action: { label: t("sessions.retry"), onClick: () => void onRevokeOthers() },
      });
    } finally {
      setRevokingOthers(false);
    }
  };

  const list = sessions ?? [];
  const otherCount = list.filter((s) => !s.is_current).length;

  return (
    <Stagger className="flex flex-col" data-testid="sessions-screen">
      <StaggerItem>
        <Slip>
          <SlipSection
            caption={t("sessions.title")}
            note={t("sessions.lede")}
            headingAs="h2"
          >
            {loadError ? (
              <SlipMessage
                tone="error"
                role="alert"
                testId="sessions-load-error"
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void load()}
                    data-testid="sessions-retry"
                  >
                    {t("sessions.retry")}
                  </Button>
                }
              >
                {t("sessions.loadError")}
              </SlipMessage>
            ) : sessions === null ? (
              <div
                className="g-ruled"
                data-testid="sessions-loading"
                aria-live="polite"
                aria-busy="true"
              >
                <span className="sr-only">{t("sessions.loading")}</span>
                {[0, 1].map((i) => (
                  <div key={i} className="flex flex-col gap-2 px-4 py-4 md:px-5">
                    <Skeleton w="42%" h={14} />
                    <Skeleton w="64%" h={12} />
                  </div>
                ))}
              </div>
            ) : list.length === 0 ? (
              <SlipMessage tone="muted" testId="sessions-empty">
                {t("sessions.empty")}
              </SlipMessage>
            ) : (
              <ul
                className="g-ruled"
                data-testid="sessions-list"
                aria-label={t("sessions.title")}
              >
                {list.map((s) => {
                  const device =
                    describeDevice(s.user_agent) ?? t("sessions.unknownDevice");
                  return (
                    <li
                      key={s.family_id}
                      data-testid={"session-row-" + s.family_id}
                      // The raw user-agent stays reachable for anyone who wants
                      // it, without spending the row on it.
                      title={s.user_agent ?? undefined}
                      className={clsx(
                        "flex items-center gap-3 px-4 py-4 md:px-5",
                        // The current session is marked by the BADGE (a real
                        // word, readable in greyscale); the soft wash is only a
                        // second, redundant cue. No coloured edge stripe — that
                        // is the generated-UI tell this rebuild refuses.
                        s.is_current && "bg-[var(--g-primary-soft)]/50",
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="truncate text-[14.5px] font-semibold text-foreground">
                            {device}
                          </span>
                          {s.is_current ? (
                            <span
                              className="rounded-[var(--g-radius-pill)] border border-[var(--g-primary)] px-2 py-px text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--g-primary-ink)]"
                              data-testid={"session-current-" + s.family_id}
                            >
                              {t("sessions.current")}
                            </span>
                          ) : null}
                        </div>
                        {/* The figures. Mono + tabular so a column of sessions
                            aligns on the IP and the timestamp. */}
                        <div className="g-data mt-1 flex flex-wrap items-center gap-x-2 text-[12px] leading-snug text-[var(--g-ink-2)]">
                          {s.ip_masked != null ? (
                            <>
                              <span>{s.ip_masked}</span>
                              <span aria-hidden="true">·</span>
                            </>
                          ) : null}
                          <span>{formatTimestamp(s.last_used_at, i18n.language)}</span>
                        </div>
                      </div>
                      {!s.is_current ? (
                        <Button
                          variant="outline"
                          size="sm"
                          loading={revokingId === s.family_id}
                          onClick={() => void onRevoke(s)}
                          aria-label={t("sessions.revokeAria", { device })}
                          data-testid={"session-revoke-" + s.family_id}
                        >
                          {revokingId === s.family_id
                            ? t("sessions.revoking")
                            : t("sessions.revoke")}
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </SlipSection>
        </Slip>
      </StaggerItem>

      {otherCount > 0 ? (
        <StaggerItem>
          <DangerZone
            title={t("sessions.dangerTitle")}
            note={t("sessions.logoutEverywhereNote")}
          >
            <SlipRow
              testId="sessions-logout-everywhere"
              onClick={() => void onRevokeOthers()}
              hideChevron
              danger
              disabled={revokingOthers}
              label={
                revokingOthers
                  ? t("sessions.logoutEverywhereBusy")
                  : t("sessions.logoutEverywhere")
              }
              // `devices`, not `count` — `count` would hand the string to
              // i18next's plural resolver and need four RU plural forms.
              hint={t("sessions.logoutEverywhereHint", { devices: otherCount })}
            />
          </DangerZone>
        </StaggerItem>
      ) : null}
    </Stagger>
  );
}

export default SessionsScreen;
